// FbxAnimWizard — uploads a source animation FBX and converts it server-side.
// Body (ENG-661): Blender → body GLB. Face (ENG-676): python → ARKit face JSON.
// Conversion is async: we POST the FBX, get a "converting" row, then poll until
// it's "ready" or "error".

import React, { useState } from 'react'
import WizardShell, { inputStyle, labelStyle, tokens } from './WizardShell'
import { CustomAssetManager } from '../../../lib/CustomAssetManager'
import { assetRegistry } from '../../../lib/assetRegistry'
import { defaultFbxAnimKind } from '../../../lib/fbxAnimKind'

function baseName(file) {
  return (file?.name ?? '').replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_\-]+/g, '_')
}

export default function FbxAnimWizard({ file, path = [], onDone, onCancel }) {
  const [name, setName] = useState(baseName(file))
  const [kind, setKind] = useState(defaultFbxAnimKind(file?.name)) // 'body' | 'face'
  // Not authored — see the note in GenerateBodyAnimWizard. 'other' is the
  // server default for animation_assets.category; nothing branches on it.
  const category = 'other'
  const [additive, setAdditive] = useState(false)
  const [loopable, setLoopable] = useState(false)
  const [error, setError] = useState(null)
  const [phase, setPhase] = useState('form') // 'form' | 'converting'

  const submit = async () => {
    if (!name.trim()) { setError('Name is required'); return }
    setPhase('converting'); setError(null)
    try {
      const row = await CustomAssetManager.convertAnimationAsset(file, {
        name: name.trim(),
        kind,
        category,
        additive,
        loopable,
      })
      assetRegistry.setPath(`anim:${row.id}`, path)
      const final = await CustomAssetManager.pollConversion(row.id)
      const status = final.conversion_status ?? final.conversionStatus
      if (status === 'error') {
        setError(`Conversion failed: ${final.conversion_error ?? final.conversionError ?? 'unknown error'}`)
        setPhase('form')
        return
      }
      onDone?.(final)
    } catch (e) {
      setError(String(e.message ?? e))
      setPhase('form')
    }
  }

  const converting = phase === 'converting'
  const isFace = kind === 'face'

  return (
    <WizardShell
      title="Convert animation FBX"
      subtitle={file?.name}
      onCancel={converting ? undefined : onCancel}
      onSubmit={submit}
      submitDisabled={converting || !name.trim()}
      submitLabel={converting ? 'Converting…' : 'Convert'}
      error={error}
    >
      <label style={labelStyle}>Type</label>
      <div style={{ display: 'flex', gap: 6 }}>
        {[['body', 'Body anim'], ['face', 'Face anim']].map(([v, lbl]) => (
          <button key={v} onClick={() => !converting && setKind(v)} style={pillStyle(kind === v)} disabled={converting}>
            {lbl}
          </button>
        ))}
      </div>

      <label style={labelStyle}>Name</label>
      <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} disabled={converting} autoFocus />

      {!isFace && (
        <>
          <label style={labelStyle}>Flags</label>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <Check label="Additive (layered gesture)" checked={additive} onChange={setAdditive} disabled={converting} />
            <Check label="Loopable" checked={loopable} onChange={setLoopable} disabled={converting} />
          </div>
        </>
      )}

      <div style={{ marginTop: 14, fontSize: 10, fontFamily: 'var(--m-font)', color: tokens.dim }}>
        {converting
          ? 'Converting on the server. This can take up to a minute — keep this open.'
          : isFace
            ? 'The face FBX (MetaHuman control rig) is converted server-side to an ARKit face clip (JSON) and registered as a runtime face clip.'
            : 'The FBX is converted to our standard body GLB server-side (Blender), then registered as a runtime clip. Source FBX is kept for re-conversion.'}
      </div>
    </WizardShell>
  )
}

function Check({ label, checked, onChange, disabled }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: tokens.text, fontFamily: 'var(--m-font)', fontSize: 11, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1 }}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} />
      {label}
    </label>
  )
}

function pillStyle(active) {
  return {
    padding: '4px 12px',
    borderRadius: 4,
    fontFamily: 'var(--m-font)',
    fontSize: 10,
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    background: active ? tokens.primary : 'transparent',
    border: `1px solid ${active ? tokens.primary : tokens.border}`,
    color: active ? tokens.onPrim : tokens.dim,
    cursor: 'pointer',
  }
}
